import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { FieldValuesMap } from '../../types/formConditions';

interface FieldValuesContextValue {
  fieldValues: FieldValuesMap;
  updateFieldValue: (fieldId: string, value: string | string[] | number | boolean) => void;
  hiddenItemIds: Set<string>;
  setFieldHidden: (itemId: string, isHidden: boolean) => void;
}

const FieldValuesContext = createContext<FieldValuesContextValue | undefined>(undefined);

interface FieldValuesProviderProps {
  initialFieldValues: FieldValuesMap;
  fieldKeyMap: { [itemId: string]: string };
  children: ReactNode;
}

export const FieldValuesProvider: React.FC<FieldValuesProviderProps> = ({
  initialFieldValues,
  fieldKeyMap,
  children,
}) => {
  const [fieldValues, setFieldValues] = useState<FieldValuesMap>(initialFieldValues);
  const [hiddenItemIds, setHiddenItemIds] = useState<Set<string>>(new Set());

  const updateFieldValue = useCallback(
    (fieldId: string, value: string | string[] | number | boolean) => {
      const fieldKey = fieldKeyMap[fieldId];

      if (!fieldKey) {
        return;
      }

      setFieldValues((prev: FieldValuesMap) => ({ ...prev, [fieldKey]: value }));
    },
    [fieldKeyMap]
  );

  const setFieldHidden = useCallback((itemId: string, isHidden: boolean) => {
    setHiddenItemIds((prev) => {
      if (isHidden) {
        prev.add(itemId);
      } else {
        prev.delete(itemId);
      }
      return prev;
    });
  }, []);

  return (
    <FieldValuesContext.Provider
      value={{ fieldValues, updateFieldValue, hiddenItemIds, setFieldHidden }}
    >
      {children}
    </FieldValuesContext.Provider>
  );
};

export const useFieldValues = (): FieldValuesContextValue => {
  const context = useContext(FieldValuesContext);
  if (!context) {
    throw new Error('useFieldValues must be used within a FieldValuesProvider');
  }
  return context;
};
